/**
 * Reading Room sound palette — the variant's bespoke SFX.
 *
 * Every sound here is composed from the primitives in `juice/audio.ts`. The
 * Reading Room flavor is warm, never bright; rounded sine + triangle waves,
 * gentle envelopes, no buzzers. The "incorrect" feedback is always a low,
 * calm settle tone — never a harsh sound.
 *
 * Other variants will have their own `sound.ts` with their own palette
 * (e.g. a Field Day variant might lean on chip-tune saws). The toolbox is
 * shared; the *taste* is variant-local.
 *
 * Every public play* function routes through `audio.playIfNotMuted` so the
 * Preferences mute toggle works automatically.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

/** Reading Room SFX vocabulary. */
export type ReadingRoomSoundName =
  | 'tap'
  | 'tap-soft'
  | 'select'
  | 'correct'
  | 'incorrect-soft'
  | 'reveal'
  | 'celebrate'
  | 'celebrate-big'
  | 'tick'
  | 'whoosh'
  | 'chime'
  | 'page-turn';

/**
 * Optional per-sound haptic style. Variants may mirror sounds with haptics
 * via `triggerHaptic` — this is the recommended mapping for the Reading Room.
 */
export const soundHaptic: Partial<Record<ReadingRoomSoundName, HapticStyle>> = {
  tap: 'light',
  'tap-soft': 'light',
  select: 'medium',
  correct: 'success',
  'incorrect-soft': 'light',
  reveal: 'light',
  celebrate: 'success',
  'celebrate-big': 'success',
  tick: 'light',
  chime: 'light',
  'page-turn': 'light',
};

// ---------------------------------------------------------------------------
// Synthesis — each function composes primitives from juice/audio.ts.
// ---------------------------------------------------------------------------

function synthesizeWoodTap(soft = false): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const dur = soft ? 0.08 : 0.11;

  // Filtered noise body.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 120 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = soft ? 600 : 900;
  bp.Q.value = soft ? 3 : 5;

  const { node: noiseGain } = audio.envelope(ctx, {
    attack: 0.002,
    decay: 0.03,
    sustain: 0.3,
    release: dur,
    peak: soft ? 0.18 : 0.32,
  });

  audio.connectThrough(noise, bp, noiseGain, master);
  noise.start(t);
  noise.stop(t + dur + 0.05);

  // Pitched "wood" component.
  const osc = audio.makeOscillator(ctx, { freq: soft ? 320 : 420, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(soft ? 200 : 240, t + dur);
  const { node: oscGain } = audio.envelope(ctx, {
    attack: 0.002,
    decay: 0.02,
    sustain: 0.2,
    release: dur,
    peak: soft ? 0.1 : 0.18,
  });
  audio.connectThrough(osc, oscGain, master);
  osc.start(t);
  osc.stop(t + dur + 0.05);
}

function synthesizeSelect(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 360, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(540, t + 0.08);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.005,
    decay: 0.05,
    sustain: 0.2,
    release: 0.12,
    peak: 0.28,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.2);

  // Layer a tiny tactile click.
  synthesizeWoodTap(true);
}

function synthesizeCorrect(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t0 = ctx.currentTime;
  // C5 → E5 → G5 — soft major triad.
  const notes = [523.25, 659.25, 783.99];
  notes.forEach((freq, i) => {
    const t = t0 + i * 0.06;

    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const osc2 = audio.makeOscillator(ctx, { freq: freq * 2, type: 'triangle' });

    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2400, t);
    lp.Q.value = 0.5;

    const g = ctx.createGain();
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.22, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.22 * 0.4, t + 0.09);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.59);

    const g2 = ctx.createGain();
    g2.gain.value = 0.08; // shimmer is quiet

    osc.connect(g);
    osc2.connect(g2);
    g.connect(lp);
    g2.connect(lp);
    lp.connect(master);
    osc.start(t);
    osc2.start(t);
    osc.stop(t + 0.7);
    osc2.stop(t + 0.7);
  });
}

function synthesizeIncorrectSoft(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc1 = audio.makeOscillator(ctx, { freq: 220, type: 'sine' });
  osc1.frequency.exponentialRampToValueAtTime(174.61, t + 0.5);
  const osc2 = audio.makeOscillator(ctx, { freq: 330, type: 'sine' });
  osc2.frequency.exponentialRampToValueAtTime(261.63, t + 0.5);

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 1400;
  lp.Q.value = 0.4;

  const { node: g1 } = audio.envelope(ctx, {
    attack: 0.03,
    decay: 0.1,
    sustain: 0.5,
    release: 0.6,
    peak: 0.18,
  });
  const { node: g2 } = audio.envelope(ctx, {
    attack: 0.03,
    decay: 0.1,
    sustain: 0.5,
    release: 0.5,
    peak: 0.1,
  });

  osc1.connect(g1);
  osc2.connect(g2);
  g1.connect(lp);
  g2.connect(lp);
  lp.connect(master);
  osc1.start(t);
  osc2.start(t);
  osc1.stop(t + 0.75);
  osc2.stop(t + 0.65);
}

function synthesizeReveal(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 440, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(660, t + 0.18);
  const { node: g } = audio.envelope(ctx, {
    attack: 0.02,
    decay: 0.06,
    sustain: 0.4,
    release: 0.25,
    peak: 0.2,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.35);
}

function synthesizeCelebrate(big = false): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t0 = ctx.currentTime;
  const notes = big ? [523.25, 659.25, 783.99, 1046.5] : [523.25, 659.25, 783.99];
  const step = big ? 0.07 : 0.06;
  notes.forEach((freq, i) => {
    const t = t0 + i * step;
    const osc = audio.makeOscillator(ctx, { freq, type: 'triangle' });
    const g = ctx.createGain();
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(big ? 0.22 : 0.18, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.045);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    osc.connect(g);
    g.connect(master);
    osc.start(t);
    osc.stop(t + 0.45);
  });
}

function synthesizeTick(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 1200, type: 'sine' });
  const { node: g } = audio.envelope(ctx, {
    attack: 0.001,
    release: 0.04,
    peak: 0.08,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.05);
}

function synthesizeWhoosh(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 450 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 1.5;
  bp.frequency.setValueAtTime(400, t);
  bp.frequency.exponentialRampToValueAtTime(2400, t + 0.3);
  bp.frequency.exponentialRampToValueAtTime(800, t + 0.45);

  const { node: g } = audio.envelope(ctx, {
    attack: 0.02,
    decay: 0.1,
    sustain: 0.6,
    release: 0.35,
    peak: 0.18,
  });

  audio.connectThrough(noise, bp, g, master);
  noise.start(t);
  noise.stop(t + 0.5);
}

function synthesizeChime(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const fundamental = 698.46; // F5

  [1, 2, 3].forEach((mul, i) => {
    const osc = audio.makeOscillator(ctx, {
      freq: fundamental * mul * (i === 0 ? 1 : 1.002),
      type: 'sine',
    });
    const g = ctx.createGain();
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.18 / (i + 1), t + 0.005);
    g.gain.exponentialRampToValueAtTime((0.18 / (i + 1)) * 0.3, t + 0.105);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.805 + i * 0.15);
    audio.connectThrough(osc, g, master);
    osc.start(t);
    osc.stop(t + 1.0);
  });
}

function synthesizePageTurn(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const dur = 0.55;
  const buf = audio.noiseBuffer(ctx, { lengthMs: 550, shaping: 1.7 });
  const noise = ctx.createBufferSource();
  noise.buffer = buf;

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.setValueAtTime(800, t);
  hp.Q.value = 0.7;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(1800, t);
  bp.frequency.exponentialRampToValueAtTime(700, t + dur);
  bp.Q.value = 1.2;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.05,
    decay: 0.1,
    sustain: 0.55,
    release: dur,
    peak: 0.22,
  });

  audio.connectThrough(noise, hp, bp, g, master);
  noise.start(t);
  noise.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class ReadingRoomSound {
  /** Lazy unlock — call from a known user gesture (Begin button, hub tap). */
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: ReadingRoomSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'tap':
          synthesizeWoodTap(false);
          return;
        case 'tap-soft':
          synthesizeWoodTap(true);
          return;
        case 'select':
          synthesizeSelect();
          return;
        case 'correct':
          synthesizeCorrect();
          return;
        case 'incorrect-soft':
          synthesizeIncorrectSoft();
          return;
        case 'reveal':
          synthesizeReveal();
          return;
        case 'celebrate':
          synthesizeCelebrate(false);
          return;
        case 'celebrate-big':
          synthesizeCelebrate(true);
          return;
        case 'tick':
          synthesizeTick();
          return;
        case 'whoosh':
          synthesizeWhoosh();
          return;
        case 'chime':
          synthesizeChime();
          return;
        case 'page-turn':
          synthesizePageTurn();
          return;
      }
    });
  }
}

export const sound = new ReadingRoomSound();
